import { get } from '@/utils/lodash';

/**
 * Stock rules for a cart line.
 *
 * The cart used to block checkout whenever `quantity.inStock` was lower than the
 * quantity ordered. That is stricter than the rule the product page applies when
 * it decides whether the item can be bought at all (`notAvailableProduct` in
 * `add-to-cart.vue`), and the two drifted: most of the catalogue reports
 * `inStock: 0` — including everything sold as a pre-order against a confirmed
 * incoming shipment — so items the product page happily sells landed in the cart
 * and then dead-ended it with a modal telling the customer to reduce a quantity
 * they could not reduce. Both rules live here now so they cannot drift again.
 */

/** `quantity` sits at the top level on inventory-search results and under `erpProduct` on the detail page. */
function stockOf(product) {
  const inStock = get(product, 'quantity.inStock');
  return inStock === undefined ? get(product, 'erpProduct.quantity.inStock') : inStock;
}

/** A confirmed purchase order means the item is sold as a pre-order, not against on-hand stock. */
function hasIncomingShipment(product) {
  return get(product, 'itemToCome.receptionPlanningDate') != null
    || get(product, 'erpProduct.itemToCome.receptionPlanningDate') != null;
}

/** Whether on-hand stock gates this product at all. */
export function isStockGated(product) {
  // The API not reporting stock is not the same as reporting none of it.
  if (stockOf(product) == null) return false;
  if (hasIncomingShipment(product)) return false;
  return true;
}

/** Units missing for `requestedQty`, or 0 when the line is fine. */
export function stockShortfall(product, requestedQty) {
  if (!isStockGated(product)) return 0;
  const available = stockOf(product) || 0;
  const requested = Number(requestedQty) || 0;
  return requested > available ? requested - available : 0;
}

/** Gated with nothing on hand: the customer cannot fix this by lowering the quantity. */
export function isSoldOut(product) {
  return isStockGated(product) && !(stockOf(product) > 0);
}

export default { isStockGated, stockShortfall, isSoldOut };
